import * as React from "react";
import {useStructureConfig} from "@/context/StructureConfigProvider";
import {Checkbox, Grid, Tooltip} from "@mui/material";
import {FormattedMessage, defineMessages} from "react-intl";
import {OutgoingCallBarring} from "@/api";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {createMasterBarrings} from "@/tools";

type Props = {
    masterBarrings?: {
        admin?: OutgoingCallBarring | OutgoingCallBarring[];
        operator?: OutgoingCallBarring | OutgoingCallBarring[];
    };

    userBarrings?: OutgoingCallBarring | OutgoingCallBarring[];
    adminBarrings?: OutgoingCallBarring | OutgoingCallBarring[];
    operatorBarrings?: OutgoingCallBarring | OutgoingCallBarring[];

    updateUserBarrings?: (barrings: OutgoingCallBarring[]) => void;
    updateAdminBarrings?: (barrings: OutgoingCallBarring[]) => void;
    updateOperatorBarrings?: (barrings: OutgoingCallBarring[]) => void;

    disabledUser?: boolean;
    disabledAdmin?: boolean;
    disabledOperator?: boolean;
};

const cellStyle: React.CSSProperties = {
    display: "flex",
    justifyContent: "center",
};

// Static messages for extraction by formatjs/cli
// Strongly-typed map (exhaustive over OutgoingCallBarring) + static literals for formatjs/cli
const OUTGOING_CALL_BARRING_MESSAGES = defineMessages<OutgoingCallBarring>({
    [OutgoingCallBarring.Custom_1]: {id: "OutgoingCallBarring.CUSTOM_1", defaultMessage: "Custom numbers #1"},
    [OutgoingCallBarring.Custom_2]: {id: "OutgoingCallBarring.CUSTOM_2", defaultMessage: "Custom numbers #2"},
    [OutgoingCallBarring.Custom_3]: {id: "OutgoingCallBarring.CUSTOM_3", defaultMessage: "Custom numbers #3"},
    [OutgoingCallBarring.DomesticMobile]: {
        id: "OutgoingCallBarring.DOMESTIC_MOBILE",
        defaultMessage: "Domestic mobile numbers",
    },
    [OutgoingCallBarring.Donation]: {id: "OutgoingCallBarring.DONATION", defaultMessage: "Donation numbers"},
    [OutgoingCallBarring.Emergency]: {id: "OutgoingCallBarring.EMERGENCY", defaultMessage: "Emergency numbers"},
    [OutgoingCallBarring.Information]: {id: "OutgoingCallBarring.INFORMATION", defaultMessage: "Information"},
    [OutgoingCallBarring.International]: {
        id: "OutgoingCallBarring.INTERNATIONAL",
        defaultMessage: "International numbers",
    },
    [OutgoingCallBarring.LocalBlue]: {id: "OutgoingCallBarring.LOCAL_BLUE", defaultMessage: "Local/blue numbers"},
    [OutgoingCallBarring.NonLocal]: {id: "OutgoingCallBarring.NON_LOCAL", defaultMessage: "Nonlocal numbers"},
    [OutgoingCallBarring.OtherOperator]: {
        id: "OutgoingCallBarring.OTHER_OPERATOR",
        defaultMessage: "Other operator numbers",
    },
    [OutgoingCallBarring.Premium]: {id: "OutgoingCallBarring.PREMIUM", defaultMessage: "Premium numbers"},
});

const createCheckboxFactory =
    (
        disabled: boolean,
        value: OutgoingCallBarring | OutgoingCallBarring[],
        update: (value: OutgoingCallBarring[]) => void,
        masterValue: OutgoingCallBarring | OutgoingCallBarring[] = [],
    ) =>
    (item: OutgoingCallBarring) => {
        const values = value instanceof Array ? value : [value];
        return (
            <Checkbox
                color="primary"
                checked={values.includes(item) || masterValue.includes(item)}
                disabled={disabled || masterValue.includes(item)}
                onChange={(event) => {
                    let items = [...values];

                    if (event.target.checked) {
                        items.push(item);
                    } else {
                        items = items.filter((_value, _index, _array) => _value !== item);
                    }
                    update(items);
                }}
                name={item}
            />
        );
    };

type CheckboxGenerator = ReturnType<typeof createCheckboxFactory>;

export default (props: Props) => {
    const config = useStructureConfig();
    const availableOutgoingCallBarrings = config.getOutgoingCallBarrings();

    if (!availableOutgoingCallBarrings.length) return null;

    const checkboxGenerators: CheckboxGenerator[] = [];
    if (props.updateOperatorBarrings)
        checkboxGenerators.push(
            createCheckboxFactory(
                props.disabledOperator,
                props.operatorBarrings,
                props.updateOperatorBarrings,
                props.masterBarrings?.operator,
            ),
        );

    if (props.updateAdminBarrings)
        checkboxGenerators.push(
            createCheckboxFactory(
                props.disabledAdmin,
                props.adminBarrings,
                props.updateAdminBarrings,
                createMasterBarrings(
                    props.masterBarrings?.operator,
                    props.masterBarrings?.admin,
                    props.operatorBarrings,
                ),
            ),
        );

    if (props.updateUserBarrings)
        checkboxGenerators.push(
            createCheckboxFactory(
                props.disabledUser,
                props.userBarrings,
                props.updateUserBarrings,
                createMasterBarrings(
                    props.masterBarrings?.operator,
                    props.masterBarrings?.admin,
                    props.operatorBarrings,
                    props.adminBarrings,
                ),
            ),
        );

    const TooltipTitle: React.FC<{children: React.ReactNode}> = ({children}) => (
        <span style={{fontSize: "0.75rem"}}>{children}</span>
    );

    function createHeader() {
        return (
            <Grid container size={12}>
                <Grid size={6}></Grid>
                {Boolean(props.updateOperatorBarrings) && (
                    <Grid style={cellStyle} size={1}>
                        <Tooltip
                            title={
                                <TooltipTitle>
                                    <FormattedMessage id="operator_label" defaultMessage="Operator" />
                                </TooltipTitle>
                            }
                            placement="top"
                            arrow
                        >
                            <span>
                                <FontAwesomeIcon icon="user-shield" size="lg" />
                            </span>
                        </Tooltip>
                    </Grid>
                )}
                {Boolean(props.updateAdminBarrings) && (
                    <Grid style={cellStyle} size={1}>
                        <Tooltip
                            title={
                                <TooltipTitle>
                                    <FormattedMessage id="admin_label" defaultMessage="Admin" />
                                </TooltipTitle>
                            }
                            placement="top"
                            arrow
                        >
                            <span>
                                <FontAwesomeIcon icon="user-tie" size="lg" />
                            </span>
                        </Tooltip>
                    </Grid>
                )}
                {Boolean(props.updateUserBarrings) && (
                    <Grid style={cellStyle} size={1}>
                        <Tooltip
                            title={
                                <TooltipTitle>
                                    <FormattedMessage id="user_label" defaultMessage="User" />
                                </TooltipTitle>
                            }
                            placement="top"
                            arrow
                        >
                            <span>
                                <FontAwesomeIcon icon="user" size="lg" />
                            </span>
                        </Tooltip>
                    </Grid>
                )}
            </Grid>
        );
    }

    function createRows() {
        return availableOutgoingCallBarrings.map((item) => {
            const descriptor = OUTGOING_CALL_BARRING_MESSAGES[item];
            return (
                <Grid key={item} container size={12}>
                    <Grid style={{paddingTop: 9}} size={6}>
                        <span style={{fontSize: 16}}>
                            <FormattedMessage {...descriptor} />
                        </span>
                    </Grid>
                    {checkboxGenerators.map((generate, idx) => (
                        <Grid key={idx} style={cellStyle} size={1}>
                            {generate(item)}
                        </Grid>
                    ))}
                </Grid>
            );
        });
    }

    return (
        <Grid container>
            {createHeader()}
            {createRows()}
        </Grid>
    );
};
